import React, { useState, useEffect, useRef } from 'react';
import SEO from '../../components/SEO';
import PageBanner from '../../components/PageBanner';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';

const timelineImages = [
    { image: '/assets/images/about/history/fortune500.jpeg' },
    { image: '/assets/images/about/history/baic-licensed.jpeg' },
    { image: '/assets/images/about/history/foton-deepened.png' },
    { image: '/assets/images/about/history/magna.jpeg' },
    { image: '/assets/images/about/history/military-parade.jpeg' },
    { image: '/assets/images/about/history/benz.jpeg' },
    { image: '/assets/images/about/history/baic-intl.jpeg' },
    { image: '/assets/images/about/history/inalfa.jpeg' },
    { image: '/assets/images/about/history/saab.jpeg' },
    { image: '/assets/images/about/history/2003.jpeg' },
    { image: '/assets/images/about/history/2002.jpeg' },
    { image: '/assets/images/about/history/1996.jpeg' },
    { image: '/assets/images/about/history/1983.jpeg' },
    { image: '/assets/images/about/history/1966.jpeg' },
    { image: '/assets/images/about/history/1958.jpeg' }
];

const History = () => {
    const { language } = useLanguage();
    const t = translations[language].history;

    // Merge translations with static images
    const timelineEvents = t.timeline.map((event, index) => ({
        ...event,
        image: timelineImages[index].image
    }));

    const [activeImage, setActiveImage] = useState(timelineEvents[0].image);
    const eventRefs = useRef([]);

    useEffect(() => {
        const observers = [];
        const currentRefs = eventRefs.current;

        const observerCallback = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const index = Number(entry.target.dataset.index);
                    setActiveImage(timelineEvents[index].image);
                }
            });
        };

        const observerOptions = {
            root: null,
            rootMargin: '-40% 0px -40% 0px', // Trigger when element is in the middle 20% of screen
            threshold: 0
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        currentRefs.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => {
            currentRefs.forEach((ref) => {
                if (ref) observer.unobserve(ref);
            });
        };
    }, [timelineEvents]);

    return (
        <div className="bg-white min-h-screen pt-20 lg:pt-[120px]">
            {/* Hero Section */}
            <SEO
                title="BAIC History Oman | Brand Heritage & Milestones"
                description="Discover the BAIC history with brand heritage, milestones, innovation."
                canonicalUrl="https://baicoman.com/history/baic"
            />
            <h1 className="sr-only">BAIC milestones and achievements</h1>
            {/* Hero Section */}
            <PageBanner
                image="/assets/images/about/history/history-page-heading.D8-h8Ylg.png"
                imageAlt="BAIC History"
            >
                <h4 className="text-sm font-bold text-gray-300 uppercase tracking-widest mb-3">{t.title}</h4>
                <h1 className="text-2xl md:text-7xl font-bold text-white uppercase tracking-tight">{t.pageTitle}</h1>
            </PageBanner>

            <div className="flex flex-col lg:flex-row">

                {/* Left Side: Scrollable Timeline */}
                <div className="w-full lg:w-[45%] bg-white px-4 md:px-12 py-20">
                    <div className="max-w-xl mx-auto space-y-32">
                        {timelineEvents.map((event, index) => (
                            <div
                                key={index}
                                ref={el => eventRefs.current[index] = el}
                                data-index={index}
                                className="group transition-opacity duration-500"
                            >
                                <span className={`text-6xl md:text-8xl font-black mb-6 block transition-colors duration-300 ${activeImage === event.image ? 'text-red-600' : 'text-gray-200'}`}>
                                    {event.year}
                                </span>
                                <p className={`text-xl md:text-2xl font-medium leading-relaxed transition-colors duration-300 ${activeImage === event.image ? 'text-gray-900' : 'text-gray-400'}`}>
                                    {event.title}
                                </p>
                                {/* Mobile Image (Visible only on small screens) */}
                                <div className="lg:hidden mt-8 aspect-video rounded-xl overflow-hidden shadow-lg">
                                    <img src={event.image} alt={event.year} className="w-full h-full object-cover" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right Side: Sticky Image */}
                <div className="hidden lg:block w-[55%] h-screen sticky top-0 bg-gray-100 overflow-hidden">
                    <div className="relative w-full h-full">
                        {timelineEvents.map((event, index) => (
                            <img
                                key={index}
                                src={event.image}
                                alt={event.year}
                                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${activeImage === event.image ? 'opacity-100 scale-105' : 'opacity-0 scale-100'}`}
                            />
                        ))}
                        {/* Overlay for better integration */}
                        <div className="absolute inset-0 bg-black/10"></div>
                    </div>
                </div>

            </div>

            {/* Footer Spacer */}
            <div className="h-20 bg-white"></div>
        </div>
    );
};

export default History;
